import {
  PARTICIPATION_AND_PUBLISH_MEMBERS,
  isStageUpdateOperation,
  MIXED_STAGE_EDITS_REFUSAL,
  type BindingColumnChoiceInput,
  type UpdateOperation,
  type UpdateRequest,
} from "@/shared/domain/boards/boardAuthoringService";

import { isPlainObject } from "./boardToolSchema";

// The input reading for `client_update_board` lives here, outside the
// tool module, so the tool and its confirmation card can share it without
// an import cycle (the same split as `createSkillCoerce.ts`).

export interface UpdateBoardInput {
  board_id?: unknown;
  title?: unknown;
  time_range?: unknown;
  calendar?: unknown;
  sections?: unknown;
  operations?: unknown;
  selections?: unknown;
  stages?: unknown;
}

/** Every `op` an update operation may name; the tool's schema lists the same values. */
export const OPERATION_NAMES: readonly string[] = [
  "replace",
  "add",
  "remove",
  "set_participation_and_publish",
  "add_stage",
  "replace_stage",
  "rename_stage",
  "remove_stage",
  "set_panels_filtered_by_control",
];

/**
 * Why the input cannot be turned into an `UpdateRequest`, or the request.
 * Structural checks only, with specific messages the model can act on; the
 * authoring service checks panel ids against the board. A list member
 * (`sections`, `operations`, `selections`, `stages`) that
 * arrives as a JSON string is read as the array it parses to; text that is
 * not JSON for an array is refused with a message naming the text form, and
 * any other non-array is refused as the wrong shape.
 */
export function toUpdateRequest(
  input: UpdateBoardInput,
): { problem: string } | { request: UpdateRequest } {
  if (typeof input.board_id !== "string" || input.board_id === "") {
    return {
      problem:
        "board_id must be the id of a board; find ids with client_read_board or in misc_context.board.",
    };
  }
  if (input.title !== undefined && typeof input.title !== "string") {
    return { problem: "title must be a string." };
  }
  const sections = asList("sections", input.sections, SECTION_ITEMS);
  if ("problem" in sections) {
    return sections;
  }
  const selections = asList("selections", input.selections, SELECTION_ITEMS);
  if ("problem" in selections) {
    return selections;
  }
  if (selections.value !== undefined && !Array.isArray(selections.value)) {
    return {
      problem:
        "selections must be an array of selection declarations; send the whole list, which replaces the one the board has.",
    };
  }
  const stages = asList("stages", input.stages, STAGE_ITEMS);
  if ("problem" in stages) {
    return stages;
  }
  if (sections.value !== undefined && !Array.isArray(sections.value)) {
    return {
      problem:
        "sections must be an array: the board's whole sections list as it should be after the update, each item in client_create_board's sections shape.",
    };
  }
  if (stages.value !== undefined && !Array.isArray(stages.value)) {
    return {
      problem:
        "stages must be an array of {id, name, inputs?, body} stages; send the whole list, which replaces the one the board has.",
    };
  }
  const parsedOperations = toUpdateOperations(
    input.operations,
    "operations must be an array of panel or stage operations (it may be empty for a title, time_range, calendar, sections, selections, or stages change).",
  );
  if ("problem" in parsedOperations) {
    return parsedOperations;
  }
  const { operations } = parsedOperations;
  if (stages.value !== undefined && operations.some(isStageUpdateOperation)) {
    return { problem: MIXED_STAGE_EDITS_REFUSAL };
  }
  return {
    request: {
      boardId: input.board_id,
      ...(input.title !== undefined && { title: input.title }),
      ...(input.time_range !== undefined && { timeRange: input.time_range }),
      ...(input.calendar !== undefined && { calendar: input.calendar }),
      ...(sections.value !== undefined && { sections: sections.value }),
      operations,
      ...(selections.value !== undefined && {
        selections: selections.value as UpdateRequest["selections"],
      }),
      ...(stages.value !== undefined && { stages: stages.value }),
    },
  };
}

/**
 * The `operations` member read into panel and stage operations, or the first
 * problem with it. A list sent as JSON text is read as the array it parses
 * to. `notListProblem` is the refusal for any other value that is not an
 * array, a missing one included, so each tool can say when it accepts an
 * empty list.
 */
export function toUpdateOperations(
  value: unknown,
  notListProblem = "operations must be an array of panel or stage operations.",
): { problem: string } | { operations: UpdateOperation[] } {
  const list = asList("operations", value, OPERATION_ITEMS);
  if ("problem" in list) {
    return list;
  }
  if (!Array.isArray(list.value)) {
    return { problem: notListProblem };
  }
  const operations: UpdateOperation[] = [];
  for (const [index, entry] of list.value.entries()) {
    const parsed = toOperation(entry, index);
    if ("problem" in parsed) {
      return parsed;
    }
    operations.push(parsed.operation);
  }
  return { operations };
}

/** What each list member's items are, for the text-form refusals. */
const SECTION_ITEMS = "section objects (id, title, x, y, width, height)";
const SELECTION_ITEMS = "selection declaration objects";
const STAGE_ITEMS = "{id, name, inputs?, body} objects";
const OPERATION_ITEMS = "panel or stage operation objects";

/**
 * A list member as the model sent it, or the array a JSON string parses
 * to. The model sometimes types the list as text rather than sending the
 * array; text that is not valid JSON, or that parses to something other
 * than an array, is a problem naming the text form and what to send. Any
 * value that is not a string is handed back for the caller's shape check.
 */
function asList(
  name: string,
  value: unknown,
  items: string,
): { value: unknown } | { problem: string } {
  if (typeof value !== "string") {
    return { value };
  }
  const advice = `Send ${name} as an array of ${items}, not as a string.`;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    return {
      problem: `${name} arrived as text, not as a JSON array value; the text is not valid JSON (${reason}). ${advice}`,
    };
  }
  if (!Array.isArray(parsed)) {
    return {
      problem: `${name} arrived as text, not as a JSON array value; the text parses to ${jsonKind(parsed)}, not an array. ${advice}`,
    };
  }
  return { value: parsed };
}

/** What parsed JSON that is not an array is: `null`, `an object`, `a string`, `a number` or `a boolean`. */
function jsonKind(value: unknown): string {
  if (value === null) {
    return "null";
  }
  return typeof value === "object" ? "an object" : `a ${typeof value}`;
}

function toOperation(
  raw: unknown,
  index: number,
): { problem: string } | { operation: UpdateOperation } {
  if (!isPlainObject(raw)) {
    return { problem: `operations[${index}] must be an object.` };
  }
  const { op, panel_id: panelId, panel } = raw;
  if (typeof op !== "string" || !OPERATION_NAMES.includes(op)) {
    return {
      problem: `operations[${index}].op must be one of ${OPERATION_NAMES.map((name) => `"${name}"`).join(", ")}.`,
    };
  }
  const allowed = operationMembers(op);
  // Every panel operation that takes no `panel` has its own refusal of one,
  // below, which says what to send instead.
  const unexpected = Object.keys(raw).filter(
    (key) =>
      !allowed.includes(key) &&
      !(key === "panel" && allowed.includes("panel_id")),
  );
  if (unexpected.length > 0) {
    return {
      problem: `operations[${index}] (${op}) must not carry ${unexpected.join(", ")}; allowed members are ${allowed.join(", ")}.`,
    };
  }
  if (op.endsWith("_stage")) {
    return toStageOperation(raw, index);
  }
  if (op === "set_panels_filtered_by_control") {
    return toSetPanelsFilteredByControl(raw, index);
  }
  if (typeof panelId !== "string" || panelId === "") {
    return { problem: `operations[${index}].panel_id must be a panel id.` };
  }
  if (op === "remove") {
    if (panel !== undefined) {
      return {
        problem: `operations[${index}] removes panel "${panelId}" and must not carry a panel.`,
      };
    }
    return { operation: { op, panel_id: panelId } };
  }
  if (op === "set_participation_and_publish") {
    return toSetParticipationAndPublish(raw, index, panelId);
  }
  if (!isPlainObject(panel)) {
    return {
      problem: `operations[${index}] (${op} "${panelId}") needs a panel object in client_create_board's panel shape.`,
    };
  }
  return {
    operation: { op: op as "replace" | "add", panel_id: panelId, panel },
  };
}

/** The members an operation named `op` may carry. */
function operationMembers(op: string): readonly string[] {
  switch (op) {
    case "add_stage":
      return ["op", "stage_id", "stage", "after_stage_id"];
    case "replace_stage":
      return ["op", "stage_id", "stage"];
    case "rename_stage":
      return ["op", "stage_id", "name"];
    case "remove_stage":
      return ["op", "stage_id"];
    case "remove":
      return ["op", "panel_id"];
    case "set_participation_and_publish":
      return ["op", "panel_id", "participation", "publish"];
    case "set_panels_filtered_by_control":
      return ["op", "control_id", "panel_ids", "columns"];
    default:
      return ["op", "panel_id", "panel"];
  }
}

function toStageOperation(
  raw: Record<string, unknown>,
  index: number,
): { problem: string } | { operation: UpdateOperation } {
  const { op, stage_id: id, stage, name, after_stage_id: afterId } = raw;
  if (typeof id !== "string" || id === "") {
    return { problem: `operations[${index}].stage_id must be a stage id.` };
  }
  if (op === "remove_stage") {
    return { operation: { op, stage_id: id } };
  }
  if (op === "rename_stage") {
    return typeof name === "string" && name !== ""
      ? { operation: { op, stage_id: id, name } }
      : {
          problem: `operations[${index}].name must be the name to rename stage "${id}" to.`,
        };
  }
  if (!isPlainObject(stage)) {
    return {
      problem: `operations[${index}] (${String(op)} "${id}") needs a complete stage object with the same id.`,
    };
  }
  if (op === "replace_stage") {
    return { operation: { op, stage_id: id, stage } };
  }
  if (
    afterId !== undefined &&
    (typeof afterId !== "string" || afterId === "")
  ) {
    return {
      problem: `operations[${index}].after_stage_id must be the id of a stage already on the board that this update does not remove, or of a stage an earlier operation of this update adds.`,
    };
  }
  return {
    operation: {
      op: "add_stage",
      stage_id: id,
      stage,
      ...(afterId === undefined ? {} : { after_stage_id: afterId }),
    },
  };
}

/** The keys each member takes (`participatingSelectionsOf` for
 *  `participation`, the default gestures for `publish`). */
const MEMBER_KEYS: Record<
  (typeof PARTICIPATION_AND_PUBLISH_MEMBERS)[number],
  string
> = {
  participation:
    'the selections the panel takes part in ("focus" and "brush" on a chart or table, "focus" on a map; on a panel that declares interactions or selectionBindings, only the selections its selectionBindings name)',
  publish: '"focus" or "brush"',
};

const MEMBER_REMOVED_EFFECT: Record<
  (typeof PARTICIPATION_AND_PUBLISH_MEMBERS)[number],
  string
> = {
  participation:
    "the panel takes its default mode in each selection it takes part in: filter on a chart or table, highlight on a map",
  publish:
    "each gesture writes the section's selection where the panel's section declares one, else the board's",
};

/**
 * A `set_participation_and_publish` operation, or the first problem with
 * its shape: each of `participation` and `publish` must be absent, a plain
 * object, or `null` (which removes the member), at least one of the two must
 * be present, and `panel` must be absent, since the operation changes the two
 * members without resending the panel.
 */
function toSetParticipationAndPublish(
  raw: Record<string, unknown>,
  index: number,
  panelId: string,
): { problem: string } | { operation: UpdateOperation } {
  if (raw.panel !== undefined) {
    return {
      problem: `operations[${index}] (set_participation_and_publish "${panelId}") must not carry a panel; send participation, publish, or both.`,
    };
  }
  const members: {
    participation?: Record<string, unknown> | null;
    publish?: Record<string, unknown> | null;
  } = {};
  for (const member of PARTICIPATION_AND_PUBLISH_MEMBERS) {
    const value = raw[member];
    if (value === undefined) {
      continue;
    }
    if (value !== null && !isPlainObject(value)) {
      return {
        problem: `operations[${index}].${member} must be an object keyed by ${MEMBER_KEYS[member]}, or null to remove it, after which ${MEMBER_REMOVED_EFFECT[member]}.`,
      };
    }
    members[member] = value;
  }
  if (members.participation === undefined && members.publish === undefined) {
    return {
      problem: `The operation on panel "${panelId}" changes neither participation nor publish; send at least one of them, or null to remove one from the panel.`,
    };
  }
  return {
    operation: {
      op: "set_participation_and_publish",
      panel_id: panelId,
      ...members,
    },
  };
}

/**
 * A `set_panels_filtered_by_control` operation, or the first problem with
 * its shape: `control_id` a stage id, `panel_ids` a list of panel ids (the
 * whole set the control should filter, empty to filter none), and each of
 * the optional `columns` a panel id and a stage it binds with either a
 * `column` and its `column_holds`, or `leave_unfiltered: true`.
 */
function toSetPanelsFilteredByControl(
  raw: Record<string, unknown>,
  index: number,
): { problem: string } | { operation: UpdateOperation } {
  const where = `operations[${index}] (set_panels_filtered_by_control)`;
  const { control_id: controlId, panel_ids: panelIds, columns } = raw;
  if (typeof controlId !== "string" || controlId === "") {
    return {
      problem: `${where}.control_id must be the stage id of a value control.`,
    };
  }
  if (
    !Array.isArray(panelIds) ||
    panelIds.some((id) => typeof id !== "string" || id === "")
  ) {
    return {
      problem: `${where}.panel_ids must be a list of panel ids: every panel the control should filter once the update is applied, or an empty list to filter none.`,
    };
  }
  if (columns !== undefined && !Array.isArray(columns)) {
    return {
      problem: `${where}.columns must be a list of {panel_id, stage_id, column, column_holds} or {panel_id, stage_id, leave_unfiltered: true} objects.`,
    };
  }
  const choices: BindingColumnChoiceInput[] = [];
  for (const [position, entry] of (columns ?? []).entries()) {
    const choice = toColumnChoice(entry);
    if (choice === undefined) {
      return {
        problem: `${where}.columns[${position}] must be {panel_id, stage_id, column, column_holds} with column_holds "value" or "entity-id", or {panel_id, stage_id, leave_unfiltered: true}; stage_id is a stage the panel binds.`,
      };
    }
    choices.push(choice);
  }
  return {
    operation: {
      op: "set_panels_filtered_by_control",
      control_id: controlId,
      panel_ids: panelIds as string[],
      ...(columns === undefined ? {} : { columns: choices }),
    },
  };
}

function toColumnChoice(entry: unknown): BindingColumnChoiceInput | undefined {
  if (!isPlainObject(entry)) {
    return undefined;
  }
  const {
    panel_id: panelId,
    stage_id: stageId,
    column,
    column_holds: columnHolds,
    leave_unfiltered: leaveUnfiltered,
    ...rest
  } = entry;
  if (
    Object.keys(rest).length > 0 ||
    typeof panelId !== "string" ||
    typeof stageId !== "string"
  ) {
    return undefined;
  }
  if (
    leaveUnfiltered === true &&
    column === undefined &&
    columnHolds === undefined
  ) {
    return { panel_id: panelId, stage_id: stageId, leave_unfiltered: true };
  }
  if (
    leaveUnfiltered === undefined &&
    typeof column === "string" &&
    column !== "" &&
    (columnHolds === "value" || columnHolds === "entity-id")
  ) {
    return {
      panel_id: panelId,
      stage_id: stageId,
      column,
      column_holds: columnHolds,
    };
  }
  return undefined;
}
